import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  getImages,
  getImage,
  uploadImages,
  updateImage,
  deleteImage,
  bulkDeleteImages,
} from "api/imageApi"
import { VEHICLE_IMAGE_QUERY_KEY } from "./useShareVehicleImage"

export const IMAGES_QUERY_KEY = "images"

export const useImages = (params = {}) => {
  return useQuery({
    queryKey: [IMAGES_QUERY_KEY, params],
    queryFn: () => getImages(params),
    keepPreviousData: true,
  })
}

export const useImage = id => {
  return useQuery({
    queryKey: [IMAGES_QUERY_KEY, id], // unique cache key
    queryFn: () => getImage(id),
    enabled: !!id, // only run if identifier is provided
  })
}

/**
 * Extract a usable URL string from a /images/{id} API response.
 * Accepts the common Laravel response shapes as well as a bare image object:
 *   { success: true, data: { id, url, ... } }   → "..."
 *   { data: { id, url, ... } }                  → "..."
 *   { id, url, ... }                            → "..."
 *   null                                        → null
 */
export const extractImageUrl = response => {
  if (!response) return null
  const image = response?.data?.data ?? response?.data ?? response
  if (!image || typeof image !== "object") return null
  return image.url || image.path || null
}

/**
 * Resolve an image ID to its URL via GET /images/{id}, with react-query caching
 * (so a 10-employee list only fires 1 query per unique ID, and the same image
 *  is reused across list / detail / header views).
 */
export const useImageUrl = id => {
  const { data, isLoading, isError } = useImage(id)
  return {
    url: extractImageUrl(data),
    isLoading: isLoading && !!id,
    isError,
  }
}

export const useUploadImages = () => {
  return useMutation({
    mutationFn: uploadImages,
  })
}

export const useUpdateImage = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateImage,
    onSuccess: data => {
      if (data?.success) {
        // Invalidate both the specific image and the images list
        queryClient.invalidateQueries([IMAGES_QUERY_KEY])
      }
    },
  })
}

export const useDeleteImage = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteImage,
    onSuccess: data => {
      if (data?.success) {
        queryClient.invalidateQueries([IMAGES_QUERY_KEY])
      }
    },
  })
}

export const useBulkDeleteImages = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: bulkDeleteImages,
    onSuccess: data => {
      if (data?.success) {
        queryClient.invalidateQueries([IMAGES_QUERY_KEY])
      }
    },
  })
}

/**
 * Update vehicle images cache in React Query
 * @param {string|number} vehicleId - Vehicle ID
 * @param {Array} images - Array of image objects [{ id, url, ... }]
 * @param {string|number|null} auctionId - Optional auction ID
 * @param {string} queryKeyBase - Base query key for vehicle images
 */
export function updateVehicleImagesCache({
  vehicleId,
  images = [],
  auctionId = null,
  queryClient,
}) {
  const queryKey = [VEHICLE_IMAGE_QUERY_KEY, vehicleId]

  queryClient.setQueryData(queryKey, oldData => {
    // Prepare new cache data in the correct format
    const newData = {
      vehicle_id: vehicleId,
      image_ids: images.map(img => img.id),
      auction_id: auctionId,
    }

    // Merge with oldData if needed
    return oldData
      ? {
          ...oldData,
          ...newData,
        }
      : newData
  })
}
